import { ComponentProps, MouseEvent, ReactElement } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { ConfirmationModal } from '@/components/modals';

import {
  MeetingTemplateDelete,
  useMutateMeetingTemplateDelete,
} from '../../api/delete-meeting-template';
import { meetingQuery } from '../../data/query-keys';
import { useMeetingTemplateFormContext } from '../../hooks/use-meeting-template-form-context';
import { resetTemplate } from '../../store';

type DeleteMeetingTemplateDialogprops = Required<
  Pick<ComponentProps<typeof ConfirmationModal>, 'open' | 'onClose'>
> & { id: MeetingTemplateDelete['param']['id'] };

export const DeleteMeetingTemplateDialog = (
  props: DeleteMeetingTemplateDialogprops,
): ReactElement => {
  const { mutate: deleteMeetingTemplate, isPending } =
    useMutateMeetingTemplateDelete();

  const form = useMeetingTemplateFormContext();
  const handleClose = (event: MouseEvent<HTMLButtonElement>) => {
    if (isPending) return;
    props.onClose(event, 'backdropClick');
  };

  const queryClient = useQueryClient();

  const handleAccept = (event: MouseEvent<HTMLButtonElement>) => {
    deleteMeetingTemplate(
      { id: props.id },
      {
        onSuccess: () => {
          toast.success('Meeting template deleted.');

          void queryClient.invalidateQueries({
            queryKey: meetingQuery.getMeetingTypes,
          });

          resetTemplate();

          form.reset();

          handleClose(event);
        },
        onError: () => {
          toast.error('Error deleting meeting template!');
        },
      },
    );
  };

  return (
    <ConfirmationModal
      open={props.open}
      isPending={isPending}
      onClose={handleClose}
      title="Delete Meeting Template?"
      content="Are you sure you want to delete this meeting template?"
      cancelActionTitle="Cancel"
      confirmActionTitle="Yes"
      onConfirm={handleAccept}
      onCancel={handleClose}
    />
  );
};
